/**
 * UNIVERSAL PROGRESS INDICATOR SYSTEM
 * 
 * This creates consistent loading/progress indicators across all modules.
 * Every module (file uploads, text saves, form submissions) will use this
 * same system so customers always know what's happening.
 * 
 * WHAT THIS DOES:
 * - Shows a progress bar (0-100%)
 * - Displays current step message
 * - Shows spinning animation
 * - Provides screen reader announcements (accessibility)
 * - Auto-hides when complete
 */

// ===================================
// CONFIGURATION
// ===================================

const PROGRESS_CONFIG = {
  // How long to show success message before auto-hiding (milliseconds)
  successDuration: 2000,
  
  // Standard progress steps (used across all modules)
  steps: {
    init: { percent: 0, message: 'Initializing...' },
    processing: { percent: 15, message: 'Processing...' },
    security: { percent: 40, message: '🔒 Verifying security...' },
    uploading: { percent: 60, message: '☁️ Uploading...' },
    finalizing: { percent: 90, message: '✨ Finalizing...' },
    complete: { percent: 100, message: '✅ Complete!' }
  }
};

// ===================================
// MAIN PROGRESS INDICATOR CLASS
// ===================================

class UniversalProgressIndicator {
  constructor(options = {}) {
    // Set up configuration
    this.containerSelector = options.container || 'body';
    this.id = options.id || `progress-${Date.now()}`;
    this.type = options.type || 'standard'; // 'standard', 'inline', 'overlay'
    
    // Track state
    this.currentPercent = 0;
    this.currentMessage = '';
    this.element = null;
    this.isVisible = false;
  }

  /**
   * SHOW the progress indicator
   * This creates the visual element and displays it
   */
  show(message = 'Processing...', percent = 0) {
    // If already showing, just update it
    if (this.element) {
      this.update(percent, message);
      return;
    }

    // Create the progress indicator element
    this.element = this.createProgressElement();
    
    // Add it to the page
    const container = document.querySelector(this.containerSelector);
    if (container) {
      container.appendChild(this.element);
      
      // Small delay so CSS transition works
      setTimeout(() => {
        this.element.classList.add('visible');
        this.isVisible = true;
      }, 10);
    }

    // Update with initial values
    this.update(percent, message);
    
    // Announce to screen readers (accessibility)
    this.announceToScreenReader(message);
  }

  /**
   * UPDATE the progress indicator
   * Changes the percentage and message
   */
  update(percent, message) {
    if (!this.element) return;

    this.currentPercent = Math.min(100, Math.max(0, percent));
    this.currentMessage = message;

    // Update the progress bar fill
    const fillEl = this.element.querySelector('.progress-fill');
    if (fillEl) {
      fillEl.style.width = `${this.currentPercent}%`;
    }

    // Update the text message
    const textEl = this.element.querySelector('.progress-message');
    if (textEl) {
      textEl.textContent = message;
    }

    // Update the percentage display
    const percentEl = this.element.querySelector('.progress-percent');
    if (percentEl) {
      percentEl.textContent = `${Math.round(this.currentPercent)}%`;
    }

    // Announce significant updates to screen readers
    if (percent === 100 || percent % 25 === 0) {
      this.announceToScreenReader(message);
    }
  }

  /**
   * HIDE the progress indicator
   * Removes it from the page with a smooth fade out
   */
  hide(delay = 0) {
    if (!this.element) return;

    setTimeout(() => {
      if (this.element) {
        this.element.classList.remove('visible');
        
        // Wait for fade out animation, then remove from page
        setTimeout(() => {
          if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
          }
          this.element = null;
          this.isVisible = false;
        }, 300); // Match CSS transition duration
      }
    }, delay);
  }

  /**
   * SHOW SUCCESS and auto-hide
   * Displays success message then automatically removes indicator
   */
  showSuccess(message = '✅ Complete!') {
    this.update(100, message);
    this.hide(PROGRESS_CONFIG.successDuration);
  }

  /**
   * SHOW ERROR
   * Displays error message with close button
   */
  showError(message = '⚠️ Something went wrong') {
    this.update(0, message);
    const el = this.element;
    if (el) {
      el.classList.add('progress-error');
      
      // Add close button for errors
      const closeBtn = el.querySelector('.progress-close-btn');
      if (closeBtn) {
        closeBtn.style.display = 'flex';
      }
    }
  }

  /**
   * CREATE the HTML structure for progress indicator
   * This builds the actual visual element
   */
  createProgressElement() {
    const wrapper = document.createElement('div');
    wrapper.id = this.id;
    wrapper.className = `universal-progress universal-progress--${this.type}`;
    wrapper.setAttribute('role', 'status');
    wrapper.setAttribute('aria-live', 'polite');
    
    wrapper.innerHTML = `
      <div class="progress-backdrop"></div>
      <div class="progress-container">
        <button type="button" class="progress-close-btn" aria-label="Close" style="display: none;">
          <span class="close-icon">✕</span>
        </button>
        <div class="progress-content">
          <div class="progress-spinner" aria-hidden="true">
            <div class="spinner-ring"></div>
          </div>
          <div class="progress-info">
            <div class="progress-message">Processing...</div>
            <div class="progress-bar-container">
              <div class="progress-bar">
                <div class="progress-fill"></div>
              </div>
              <div class="progress-percent">0%</div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // Add click handler for close button
    const closeBtn = wrapper.querySelector('.progress-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.hide();
      });
    }
    
    return wrapper;
  }

  /**
   * ANNOUNCE to screen readers (for accessibility)
   * Helps blind users know what's happening
   */
  announceToScreenReader(message) {
    // Check if announcement element exists, create if not
    let announcer = document.getElementById('progress-announcer');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'progress-announcer';
      announcer.className = 'sr-only';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      document.body.appendChild(announcer);
    }
    
    // Update announcement
    announcer.textContent = message;
  }
}

// ===================================
// CONVENIENCE FUNCTIONS
// ===================================

/**
 * QUICK SHOW - Simple way to show progress
 * Example: showProgress('Uploading file...', 50);
 */
window.showProgress = function(message, percent = 0, options = {}) {
  if (!window._globalProgressIndicator) {
    window._globalProgressIndicator = new UniversalProgressIndicator(options);
  }
  window._globalProgressIndicator.show(message, percent);
  return window._globalProgressIndicator;
};

/**
 * UPDATE PROGRESS - Update existing progress indicator
 * Example: updateProgress(75, 'Almost done...');
 */
window.updateProgress = function(percent, message) {
  if (window._globalProgressIndicator) {
    window._globalProgressIndicator.update(percent, message);
  }
};

/**
 * HIDE PROGRESS - Hide the progress indicator
 * Example: hideProgress();
 */
window.hideProgress = function(delay = 0) {
  if (window._globalProgressIndicator) {
    window._globalProgressIndicator.hide(delay);
  }
};

/**
 * SHOW SUCCESS - Show success and auto-hide
 * Example: showProgressSuccess('Upload complete!');
 */
window.showProgressSuccess = function(message = '✅ Complete!') {
  if (window._globalProgressIndicator) {
    window._globalProgressIndicator.showSuccess(message);
  }
};

/**
 * SHOW ERROR - Show error message
 * Example: showProgressError('Upload failed. Please try again.');
 */
window.showProgressError = function(message = '⚠️ Something went wrong') {
  if (window._globalProgressIndicator) {
    window._globalProgressIndicator.showError(message);
  }
};

/**
 * STANDARD WORKFLOW HELPER
 * This function handles the common upload workflow automatically:
 * 1. Show "Processing..."
 * 2. Show "Verifying security..."
 * 3. Show "Uploading..."
 * 4. Show "Finalizing..."
 * 5. Show "Complete!" and hide
 * 
 * Usage Example:
 * await runStandardProgressWorkflow(async () => {
 *   // Your upload code here
 *   await uploadFile();
 * });
 */
window.runStandardProgressWorkflow = async function(workflowFunction, options = {}) {
  const progress = new UniversalProgressIndicator(options);
  
  try {
    // Step 1: Initialize
    progress.show(PROGRESS_CONFIG.steps.init.message, PROGRESS_CONFIG.steps.init.percent);
    await delay(300);
    
    // Step 2: Processing
    progress.update(PROGRESS_CONFIG.steps.processing.percent, PROGRESS_CONFIG.steps.processing.message);
    await delay(200);
    
    // Step 3: Security verification
    progress.update(PROGRESS_CONFIG.steps.security.percent, PROGRESS_CONFIG.steps.security.message);
    await delay(400);
    
    // Step 4: Run the actual work (upload, save, etc.)
    progress.update(PROGRESS_CONFIG.steps.uploading.percent, PROGRESS_CONFIG.steps.uploading.message);
    await workflowFunction();
    
    // Step 5: Finalizing
    progress.update(PROGRESS_CONFIG.steps.finalizing.percent, PROGRESS_CONFIG.steps.finalizing.message);
    await delay(300);
    
    // Step 6: Complete!
    progress.showSuccess(PROGRESS_CONFIG.steps.complete.message);
    
    return { success: true };
    
  } catch (error) {
    progress.showError(`⚠️ Error: ${error.message}`);
    throw error;
  }
};

/**
 * HELPER: Create a delay
 * Used to pace the progress steps so they're visible
 */
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ===================================
// EXPORT
// ===================================

// Make the class available globally
window.UniversalProgressIndicator = UniversalProgressIndicator;
window.PROGRESS_CONFIG = PROGRESS_CONFIG;

console.log('✅ Universal Progress Indicator System loaded');